import { ScrollArea } from "@/components/ui/scroll-area";
import { ComparisonsModule } from "./ComparisonsModule";
import { DocumentsModule } from "./DocumentsModule";
import { PhotosModule } from "./PhotosModule";
import { SupplementsModule } from "./SupplementsModule";
import { TasksModule } from "./TasksModule";
import { FormsModule } from "./FormsModule";
import { FinancialsModule } from "./FinancialsModule";
import { LinksModule } from "./LinksModule";
import { NeedAppraisalButton } from "./NeedAppraisalButton";
import { Comparison, ClaimDetailed } from "@/api/generated/client/models";
import type { ClaimModuleKey } from "@/constants/claim-options";
import { appraiserConfig } from "@/config/appraiser";
import { cn } from "@/lib/utils";

export type RightSidebarModuleKey =
    | "comparisons"
    | "documents"
    | "photos"
    | "financials"
    | "forms"
    | "links"
    | "tasks"
    | "supplements";

export type ExpandedRightModules = Record<RightSidebarModuleKey, boolean>;

interface RightModulesSidebarProps {
    claimId: number;
    expandedModules: ExpandedRightModules;
    onToggle: (module: RightSidebarModuleKey) => void;
    comparisons?: Comparison[];
    claim?: ClaimDetailed | null;
    canWrite: boolean;
    hiddenModules?: ClaimModuleKey[];
    className?: string;
}

export function RightModulesSidebar({
    claimId,
    expandedModules,
    onToggle,
    comparisons = [],
    claim,
    canWrite,
    hiddenModules = [],
    className,
}: RightModulesSidebarProps) {
    const appraiserEmail = appraiserConfig.email.toLowerCase();
    const appraiserAlreadyShared =
        (claim?.assignees ?? []).some((assignee) => assignee.email?.toLowerCase() === appraiserEmail) ||
        (claim?.pendingInvites ?? []).some((invite) => invite.email?.toLowerCase() === appraiserEmail);
    const showAppraisalButton = canWrite && !appraiserAlreadyShared;
    return (
        <div className={cn("w-96 border-l flex flex-col min-h-0 bg-background flex-shrink-0 overflow-hidden max-sm:h-full", className)}>
            <ScrollArea className="flex-1 w-full [&_[data-radix-scroll-area-viewport]>div]:!min-w-0 [&_[data-radix-scroll-area-viewport]>div]:!block">
                <div className="p-4 space-y-4 w-full min-w-0">
                    {showAppraisalButton && (
                        <NeedAppraisalButton claimId={claimId} hiddenModules={hiddenModules} />
                    )}
                    {!hiddenModules.includes('comparisons') && (
                        <ComparisonsModule
                            claimId={claimId}
                            isOpen={expandedModules.comparisons}
                            onToggle={() => onToggle('comparisons')}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('documents') && (
                        <DocumentsModule
                            claimId={claimId}
                            isOpen={expandedModules.documents}
                            onToggle={() => onToggle('documents')}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('financials') && (
                        <FinancialsModule
                            claimId={claimId}
                            isOpen={expandedModules.financials}
                            onToggle={() => onToggle('financials')}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('forms') && (
                        <FormsModule
                            claimId={claimId}
                            isOpen={expandedModules.forms}
                            onToggle={() => onToggle('forms')}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('links') && (
                        <LinksModule
                            claimId={claimId}
                            isOpen={expandedModules.links}
                            onToggle={() => onToggle('links')}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('photos') && (
                        <PhotosModule
                            claimId={claimId}
                            isOpen={expandedModules.photos}
                            onToggle={() => onToggle('photos')}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('supplements') && (
                        <SupplementsModule
                            claimId={claimId}
                            isOpen={expandedModules.supplements}
                            onToggle={() => onToggle('supplements')}
                            comparisons={comparisons}
                            claim={claim}
                            canWrite={canWrite}
                        />
                    )}
                    {!hiddenModules.includes('tasks') && (
                        <TasksModule
                            claimId={claimId}
                            isOpen={expandedModules.tasks}
                            onToggle={() => onToggle('tasks')}
                            canWrite={canWrite}
                        />
                    )}
                </div>
            </ScrollArea>
        </div>
    );
}
